import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { api } from '../../lib/api'
import { Store, MapPin, Phone, Mail, User, Calendar, CheckCircle, Clock, XCircle, AlertCircle, Edit, Save, X, ArrowLeft, BarChart3, Package, Star } from 'lucide-react'
import toast from 'react-hot-toast'
import AdminNavHeader from '../../components/AdminNavHeader'
import { getCache, getCacheStale, setCache } from '../../lib/cache'

const AdminStoreDetails = () => {
  const { storeId } = useParams<{ storeId: string }>()
  const navigate = useNavigate()
  const [store, setStore] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState<any>({})

  useEffect(() => {
    if (storeId) {
      loadStoreDetails()
    }
  }, [storeId])

  const loadStoreDetails = async () => {
    if (!storeId) return
    
    const cacheKey = `admin_store_details_${storeId}`
    
    // Intentar cargar desde cache primero
    const cachedStore = getCacheStale<any>(cacheKey)
    if (cachedStore) {
      setStore(cachedStore)
      setEditForm({
        name: cachedStore.name || '',
        description: cachedStore.description || '',
        address: cachedStore.address || '',
        phone: cachedStore.phone || '',
        email: cachedStore.email || '',
        latitude: cachedStore.latitude != null ? String(cachedStore.latitude) : '',
        longitude: cachedStore.longitude != null ? String(cachedStore.longitude) : '',
        delivery_fee: cachedStore.delivery_fee != null ? cachedStore.delivery_fee : 0,
        min_order_amount: cachedStore.min_order_amount != null ? cachedStore.min_order_amount : 0,
        offers_delivery: cachedStore.offers_delivery || false,
        offers_kitchen_service: cachedStore.offers_kitchen_service || false,
        is_featured: cachedStore.is_featured || false,
        development_mode: cachedStore.development_mode || false
      })
      setLoading(false)
    }
    
    try {
      setLoading(true)
      const response = await api.getAdminStore(storeId)
      const fullStore = response.data
      setStore(fullStore)
      setEditForm({
        name: fullStore.name || '',
        description: fullStore.description || '',
        address: fullStore.address || '',
        phone: fullStore.phone || '',
        email: fullStore.email || '',
        latitude: fullStore.latitude != null ? String(fullStore.latitude) : '',
        longitude: fullStore.longitude != null ? String(fullStore.longitude) : '',
        delivery_fee: fullStore.delivery_fee != null ? fullStore.delivery_fee : 0,
        min_order_amount: fullStore.min_order_amount != null ? fullStore.min_order_amount : 0,
        offers_delivery: fullStore.offers_delivery || false,
        offers_kitchen_service: fullStore.offers_kitchen_service || false,
        is_featured: fullStore.is_featured || false,
        development_mode: fullStore.development_mode || false
      })
      
      // Guardar en cache (5 minutos de TTL)
      setCache(cacheKey, fullStore, 5 * 60 * 1000)
    } catch (error: any) {
      console.error('Error loading store details:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedStore) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      toast.error('Error al cargar detalles del negocio')
      navigate('/admin/stores')
    } finally {
      setLoading(false)
    }
  }

  const handleEditStore = async () => {
    if (!store || !storeId) return

    try {
      setProcessing(true)
      
      // Preparar datos para enviar, convirtiendo y limpiando valores
      const updateData: any = {}
      
      // Solo incluir campos que tienen valores válidos
      if (editForm.name && editForm.name.trim()) {
        updateData.name = editForm.name.trim()
      }
      if (editForm.description !== undefined && editForm.description !== null) {
        const desc = editForm.description.trim()
        // Solo incluir si tiene contenido, o enviar null si está vacío
        if (desc) {
          updateData.description = desc
        } else {
          // No incluir description si está vacío, el backend lo manejará
        }
      }
      if (editForm.address && editForm.address.trim()) {
        updateData.address = editForm.address.trim()
      }
      if (editForm.phone && editForm.phone.trim()) {
        updateData.phone = editForm.phone.trim()
      }
      if (editForm.email && editForm.email.trim()) {
        updateData.email = editForm.email.trim()
      }
      
      // Convertir latitude y longitude a números, solo si tienen valores válidos
      if (editForm.latitude !== undefined && editForm.latitude !== '' && editForm.latitude !== null) {
        const lat = parseFloat(String(editForm.latitude))
        if (!isNaN(lat) && lat >= -90 && lat <= 90) {
          updateData.latitude = lat
        }
      }
      if (editForm.longitude !== undefined && editForm.longitude !== '' && editForm.longitude !== null) {
        const lng = parseFloat(String(editForm.longitude))
        if (!isNaN(lng) && lng >= -180 && lng <= 180) {
          updateData.longitude = lng
        }
      }
      
      // Convertir delivery_fee y min_order_amount a números
      if (editForm.delivery_fee !== undefined && editForm.delivery_fee !== null && editForm.delivery_fee !== '') {
        const fee = parseFloat(String(editForm.delivery_fee))
        if (!isNaN(fee) && fee >= 0) {
          updateData.delivery_fee = fee
        }
      }
      if (editForm.min_order_amount !== undefined && editForm.min_order_amount !== null && editForm.min_order_amount !== '') {
        const min = parseFloat(String(editForm.min_order_amount))
        if (!isNaN(min) && min >= 0) {
          updateData.min_order_amount = min
        }
      }
      
      // Booleanos
      if (editForm.offers_delivery !== undefined) {
        updateData.offers_delivery = Boolean(editForm.offers_delivery)
      }
      if (editForm.offers_kitchen_service !== undefined) {
        updateData.offers_kitchen_service = Boolean(editForm.offers_kitchen_service)
      }
      if (editForm.is_featured !== undefined) {
        updateData.is_featured = Boolean(editForm.is_featured)
      }
      if (editForm.development_mode !== undefined) {
        updateData.development_mode = Boolean(editForm.development_mode)
      }

      if (Object.keys(updateData).length === 0) {
        toast.error('No hay cambios para guardar')
        return
      }
      
      await api.updateAdminStore(storeId, updateData)
      toast.success('Negocio actualizado exitosamente')
      setIsEditing(false)
      await loadStoreDetails()
    } catch (error: any) {
      console.error('Error updating store:', error)
      const errorMessage = error.response?.data?.message || error.response?.data?.errors?.[0]?.msg || 'Error al actualizar el negocio'
      toast.error(errorMessage)
    } finally {
      setProcessing(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-gradient-to-r from-green-100 to-green-50 text-green-800 border-green-300'
      case 'pending': return 'bg-gradient-to-r from-yellow-100 to-yellow-50 text-yellow-800 border-yellow-300'
      case 'rejected': return 'bg-gradient-to-r from-red-100 to-red-50 text-red-800 border-red-300'
      case 'suspended': return 'bg-gradient-to-r from-orange-100 to-orange-50 text-orange-800 border-orange-300'
      default: return 'bg-gradient-to-r from-gray-100 to-gray-50 text-gray-800 border-gray-300'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <CheckCircle className="h-5 w-5" />
      case 'pending': return <Clock className="h-5 w-5" />
      case 'rejected': return <XCircle className="h-5 w-5" />
      case 'suspended': return <AlertCircle className="h-5 w-5" />
      default: return <Clock className="h-5 w-5" />
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-4 border-primary-200 border-t-primary-600 mx-auto"></div>
          <p className="mt-4 sm:mt-6 text-sm sm:text-base text-gray-600 font-semibold">Cargando detalles del negocio...</p>
        </div>
      </div>
    )
  }

  if (!store) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-gray-900 mb-2">Negocio no encontrado</h3>
          <button
            onClick={() => navigate('/admin/stores')}
            className="px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
          >
            Volver a Negocios
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pb-20 sm:pb-6">
      {/* Header */}
      <AdminNavHeader
        title="Detalles del Negocio"
        subtitle={store.name}
      />

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
        {/* Action Bar */}
        <div className="mb-4 sm:mb-6 flex items-center justify-between">
          <button
            onClick={() => navigate('/admin/stores')}
            className="flex items-center px-4 py-2 bg-white rounded-xl sm:rounded-2xl border-2 border-gray-200 hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            <span className="text-sm sm:text-base font-bold">Volver</span>
          </button>
          <div className="flex items-center gap-2 sm:gap-3">
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center px-4 sm:px-5 py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl sm:rounded-2xl hover:from-primary-700 hover:to-primary-800 transition-all duration-200 shadow-lg hover:shadow-xl active:scale-95 text-sm sm:text-base font-bold"
              >
                <Edit className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Editar
              </button>
            ) : (
              <>
                <button
                  onClick={handleEditStore}
                  disabled={processing}
                  className="flex items-center px-4 sm:px-5 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl sm:rounded-2xl hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-lg hover:shadow-xl active:scale-95 disabled:opacity-50 text-sm sm:text-base font-bold"
                >
                  <Save className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  {processing ? 'Guardando...' : 'Guardar'}
                </button>
                <button
                  onClick={() => {
                    setIsEditing(false)
                    loadStoreDetails()
                  }}
                  className="flex items-center px-4 sm:px-5 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl sm:rounded-2xl hover:from-gray-700 hover:to-gray-800 transition-all duration-200 shadow-lg hover:shadow-xl active:scale-95 text-sm sm:text-base font-bold"
                >
                  <X className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Cancelar
                </button>
              </>
            )}
          </div>
        </div>

        {/* Store Header Card */}
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl border-2 border-gray-200/60 p-4 sm:p-6 lg:p-8 mb-4 sm:mb-6">
          <div className="flex items-start gap-4 sm:gap-6">
            <div className="w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl sm:rounded-3xl flex items-center justify-center shadow-xl flex-shrink-0">
              <Store className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              {isEditing ? (
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl sm:rounded-2xl border-2 border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 text-lg sm:text-xl lg:text-2xl font-bold bg-white shadow-sm"
                />
              ) : (
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">{store.name}</h1>
              )}
              {isEditing ? (
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={3}
                  className="w-full mt-2 px-4 py-3 rounded-xl sm:rounded-2xl border-2 border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 text-sm sm:text-base bg-white shadow-sm resize-none"
                  placeholder="Descripción del negocio"
                />
              ) : (
                <p className="text-sm sm:text-base lg:text-lg text-gray-600 mb-3">{store.description || 'Sin descripción'}</p>
              )}
              <span className={`inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-bold border-2 shadow-sm ${getStatusColor(store.status || 'pending')}`}>
                {getStatusIcon(store.status || 'pending')}
                <span className="capitalize">{store.status || 'pending'}</span>
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Owner Info */}
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-lg border-2 border-gray-200/60 p-4 sm:p-6">
              <h2 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mb-4 sm:mb-5 flex items-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl sm:rounded-2xl flex items-center justify-center mr-3 shadow-lg">
                  <User className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <span>Información del Propietario</span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 text-sm sm:text-base">
                <div>
                  <span className="text-gray-500 block mb-1 font-medium">Nombre:</span>
                  <p className="font-bold text-gray-900">{store.owner?.name || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-gray-500 block mb-1 font-medium">Email:</span>
                  <p className="font-bold text-gray-900 break-all">{store.owner?.email || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-gray-500 block mb-1 font-medium">Teléfono:</span>
                  <p className="font-bold text-gray-900">{store.owner?.phone || 'No especificado'}</p>
                </div>
              </div>
            </div>

            {/* Store Details */}
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-lg border-2 border-gray-200/60 p-4 sm:p-6">
              <h2 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mb-4 sm:mb-5 flex items-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl sm:rounded-2xl flex items-center justify-center mr-3 shadow-lg">
                  <Store className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <span>Información del Negocio</span>
              </h2>
              {isEditing ? (
                <div className="space-y-4 sm:space-y-5">
                  <div>
                    <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">Dirección</label>
                    <input
                      type="text"
                      value={editForm.address}
                      onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl sm:rounded-2xl border-2 border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 text-sm sm:text-base font-medium bg-white shadow-sm"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">Teléfono</label>
                      <input
                        type="text"
                        value={editForm.phone}
                        onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl sm:rounded-2xl border-2 border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 text-sm sm:text-base font-medium bg-white shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">Email</label>
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl sm:rounded-2xl border-2 border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 text-sm sm:text-base font-medium bg-white shadow-sm"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">Latitud</label>
                      <input
                        type="number"
                        step="any"
                        value={editForm.latitude}
                        onChange={(e) => setEditForm({ ...editForm, latitude: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl sm:rounded-2xl border-2 border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 text-sm sm:text-base font-medium bg-white shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">Longitud</label>
                      <input
                        type="number"
                        step="any"
                        value={editForm.longitude}
                        onChange={(e) => setEditForm({ ...editForm, longitude: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl sm:rounded-2xl border-2 border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 text-sm sm:text-base font-medium bg-white shadow-sm"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4 text-sm sm:text-base">
                  <div className="flex items-start">
                    <MapPin className="w-5 h-5 sm:w-6 sm:h-6 mr-3 mt-0.5 text-primary-500 flex-shrink-0" />
                    <div>
                      <span className="text-gray-500 block mb-1 font-medium">Dirección:</span>
                      <p className="font-bold text-gray-900 leading-relaxed">{store.address}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Phone className="w-5 h-5 sm:w-6 sm:h-6 mr-3 text-primary-500 flex-shrink-0" />
                    <div>
                      <span className="text-gray-500 font-medium">Teléfono: </span>
                      <span className="font-bold text-gray-900">{store.phone}</span>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Mail className="w-5 h-5 sm:w-6 sm:h-6 mr-3 text-primary-500 flex-shrink-0" />
                    <div>
                      <span className="text-gray-500 font-medium">Email: </span>
                      <span className="font-bold text-gray-900 break-all">{store.email}</span>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 sm:w-6 sm:h-6 mr-3 text-primary-500 flex-shrink-0" />
                    <div>
                      <span className="text-gray-500 font-medium">Fecha de solicitud: </span>
                      <span className="font-bold text-gray-900">
                        {new Date(store.created_at).toLocaleDateString('es-DO', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Services & Pricing */}
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-lg border-2 border-gray-200/60 p-4 sm:p-6">
              <h2 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mb-4 sm:mb-5">Servicios y Tarifas</h2>
              {isEditing ? (
                <div className="space-y-4 sm:space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">Costo de Entrega (RD$)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editForm.delivery_fee}
                        onChange={(e) => setEditForm({ ...editForm, delivery_fee: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl sm:rounded-2xl border-2 border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 text-sm sm:text-base font-medium bg-white shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">Pedido Mínimo (RD$)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editForm.min_order_amount}
                        onChange={(e) => setEditForm({ ...editForm, min_order_amount: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl sm:rounded-2xl border-2 border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 text-sm sm:text-base font-medium bg-white shadow-sm"
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-4 sm:gap-6">
                    <label className="flex items-center p-3 sm:p-4 bg-gray-50 rounded-xl sm:rounded-2xl border-2 border-gray-200 hover:border-primary-300 transition-all cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editForm.offers_delivery}
                        onChange={(e) => setEditForm({ ...editForm, offers_delivery: e.target.checked })}
                        className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer mr-3"
                      />
                      <span className="text-sm sm:text-base font-bold text-gray-900">Ofrece Entrega</span>
                    </label>
                    <label className="flex items-center p-3 sm:p-4 bg-gray-50 rounded-xl sm:rounded-2xl border-2 border-gray-200 hover:border-primary-300 transition-all cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editForm.offers_kitchen_service}
                        onChange={(e) => setEditForm({ ...editForm, offers_kitchen_service: e.target.checked })}
                        className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer mr-3"
                      />
                      <span className="text-sm sm:text-base font-bold text-gray-900">Ofrece Servicio de Cocina</span>
                    </label>
                    <label className="flex items-center p-3 sm:p-4 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl sm:rounded-2xl border-2 border-yellow-300 hover:border-yellow-400 transition-all cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editForm.is_featured}
                        onChange={(e) => setEditForm({ ...editForm, is_featured: e.target.checked })}
                        className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer mr-3"
                      />
                      <span className="text-sm sm:text-base font-bold text-gray-900">⭐ Negocio Destacado</span>
                    </label>
                    <label className="flex items-center p-3 sm:p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl sm:rounded-2xl border-2 border-purple-300 hover:border-purple-400 transition-all cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editForm.development_mode}
                        onChange={(e) => setEditForm({ ...editForm, development_mode: e.target.checked })}
                        className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer mr-3"
                      />
                      <span className="text-sm sm:text-base font-bold text-gray-900">🔧 Modo Desarrollo</span>
                    </label>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                  <div className="text-center p-4 sm:p-5 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl sm:rounded-2xl border-2 border-blue-200 shadow-sm">
                    <div className="text-xs sm:text-sm text-blue-600 mb-2 font-medium">Entrega</div>
                    <div className="text-base sm:text-lg font-bold text-blue-700">
                      {store.offers_delivery ? 'Sí' : 'No'}
                    </div>
                  </div>
                  <div className="text-center p-4 sm:p-5 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl sm:rounded-2xl border-2 border-orange-200 shadow-sm">
                    <div className="text-xs sm:text-sm text-orange-600 mb-2 font-medium">Cocina</div>
                    <div className="text-base sm:text-lg font-bold text-orange-700">
                      {store.offers_kitchen_service ? 'Sí' : 'No'}
                    </div>
                  </div>
                  <div className="text-center p-4 sm:p-5 bg-gradient-to-br from-green-50 to-green-100 rounded-xl sm:rounded-2xl border-2 border-green-200 shadow-sm">
                    <div className="text-xs sm:text-sm text-green-600 mb-2 font-medium">Costo Entrega</div>
                    <div className="text-base sm:text-lg font-bold text-green-700">${store.delivery_fee || 0}</div>
                  </div>
                  <div className="text-center p-4 sm:p-5 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl sm:rounded-2xl border-2 border-purple-200 shadow-sm">
                    <div className="text-xs sm:text-sm text-purple-600 mb-2 font-medium">Pedido Mínimo</div>
                    <div className="text-base sm:text-lg font-bold text-purple-700">${store.min_order_amount || 0}</div>
                  </div>
                  <div className="text-center p-4 sm:p-5 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl sm:rounded-2xl border-2 border-yellow-200 shadow-sm">
                    <div className="text-xs sm:text-sm text-yellow-600 mb-2 font-medium">Destacado</div>
                    <div className="text-base sm:text-lg font-bold text-yellow-700">
                      {store.is_featured ? '⭐ Sí' : 'No'}
                    </div>
                  </div>
                  <div className="text-center p-4 sm:p-5 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl sm:rounded-2xl border-2 border-purple-200 shadow-sm">
                    <div className="text-xs sm:text-sm text-purple-600 mb-2 font-medium">Modo Desarrollo</div>
                    <div className="text-base sm:text-lg font-bold text-purple-700">
                      {store.development_mode ? '🔧 Sí' : 'No'}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Location */}
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-lg border-2 border-gray-200/60 p-4 sm:p-6">
              <h2 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mb-4 sm:mb-5">Ubicación</h2>
              <div className="text-sm sm:text-base space-y-2">
                <div>
                  <span className="text-gray-500 font-medium">Latitud: </span>
                  <span className="font-bold text-gray-900">{store.latitude || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-gray-500 font-medium">Longitud: </span>
                  <span className="font-bold text-gray-900">{store.longitude || 'N/A'}</span>
                </div>
                {store.latitude && store.longitude && (
                  <a
                    href={`https://www.google.com/maps?q=${store.latitude},${store.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-primary-600 hover:text-primary-700 text-sm sm:text-base font-bold mt-3 sm:mt-4"
                  >
                    <MapPin className="w-5 h-5 mr-2" />
                    Ver en Google Maps
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 sm:space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-lg border-2 border-gray-200/60 p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-4">Acciones Rápidas</h3>
              <div className="space-y-2 sm:space-y-3">
                <button
                  onClick={() => navigate(`/admin/stores/${storeId}/stats`)}
                  className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 rounded-xl sm:rounded-2xl hover:from-blue-100 hover:to-blue-200 transition-all duration-200 border-2 border-blue-200 shadow-sm hover:shadow-md active:scale-95 text-sm sm:text-base font-bold"
                >
                  <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Ver Estadísticas
                </button>
                <button
                  onClick={() => navigate(`/admin/orders?storeId=${storeId}`)}
                  className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-purple-50 to-purple-100 text-purple-700 rounded-xl sm:rounded-2xl hover:from-purple-100 hover:to-purple-200 transition-all duration-200 border-2 border-purple-200 shadow-sm hover:shadow-md active:scale-95 text-sm sm:text-base font-bold"
                >
                  <Package className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                  Ver Pedidos
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminStoreDetails

